import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { Input } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { QuickDateRangePicker } from "@whoosh/design-system/src/components/controls/QuickDateRangePicker";
import { SearchIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { useClearQueryParam } from "../../hooks/useClearQueryParam";
import { DateRange, DepartmentOrClubData } from "../../types";
import { EventsTag } from "../EventsTag";
import { NewPatronEventsSidebarFragment_club$key } from "./__generated__/NewPatronEventsSidebarFragment_club.graphql";

export type NewPatronEventsSidebarProps = {
  tmpSearch: string;
  handleSearchChange: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  fragmentRef: NewPatronEventsSidebarFragment_club$key | null;
  currentDepartmentOrClubData: DepartmentOrClubData | null;
  setCurrentDepartmentOrClubData: (value: DepartmentOrClubData) => void;
  clearFilters?: () => void;
  confirmFilters?: () => void;
  shouldShowSearchCta?: boolean;
  handleSearchPress?: () => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
};

export const NewPatronEventsSidebar = ({
  tmpSearch,
  handleSearchChange,
  dateRange,
  setDateRange,
  currentDepartmentOrClubData,
  setCurrentDepartmentOrClubData,
  fragmentRef,
  clearFilters,
  confirmFilters,
  shouldShowSearchCta,
  handleSearchPress,
  selectedTags,
  setSelectedTags,
}: NewPatronEventsSidebarProps) => {
  const data = useFragment(NewPatronEventsSidebarFragmentString, fragmentRef);

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(data ?? null);

  const clearQueryParam = useClearQueryParam();

  const toggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((tag) => tag !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const validTags =
    data?.eventTags?.edges?.filter(
      (tag) => tag?.node?.events?.count && tag?.node?.events?.count > 0
    ) ?? [];

  const departmentOptions =
    data?.departments?.edges?.filter(
      (department) => !!department?.node?.anyFacilityEventsEnabled
    ) ?? [];

  return (
    <VStack
      px={{ base: 6, md: 2 }}
      flex={1}
      height={"100%"}
      position="relative"
      testID="new-patron-events-sidebar"
    >
      <ScrollView style={{ flex: 1 }}>
        <VStack gap={2}>
          <Input<"text">
            name="Search"
            placeholder={t`Search Events`}
            value={tmpSearch}
            leftIcon={<SearchIcon />}
            onChange={handleSearchChange}
          />
          <HStack width="100%">
            <Box flex={1} flexShrink={0} height={48} maxHeight={48}>
              <QuickDateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={setDateRange}
                clubWeekStartsOn={clubWeekStartsOn}
              />
            </Box>
          </HStack>
          <Box>
            <Box py={3} px={4}>
              <Text variant="mainTiny" color="text-secondary">
                {t`DEPARTMENT`}
              </Text>
            </Box>
            <VStack gap={1}>
              <Button
                style={{ alignItems: "flex-start" }}
                accessibilityLabel={t`All departments`}
                testID="events-sidebar-department-button-all"
                variant={
                  currentDepartmentOrClubData?.id === data?.id
                    ? "secondary"
                    : "white"
                }
                size="small"
                onPress={() => {
                  setCurrentDepartmentOrClubData({
                    id: data?.id ?? null,
                    slug: null,
                  });
                  clearQueryParam("departmentSlug");
                }}
              >
                {t`All`}
              </Button>
              {departmentOptions.map((department) => (
                <Button
                  style={{ alignItems: "flex-start" }}
                  accessibilityLabel={t`${
                    department?.node?.name ?? ""
                  } department`}
                  testID={`events-sidebar-department-button-${department?.node?.id}`}
                  width="100%"
                  variant={
                    currentDepartmentOrClubData?.id === department?.node?.id
                      ? "secondary"
                      : "white"
                  }
                  size="small"
                  key={department?.node?.id}
                  onPress={() => {
                    setCurrentDepartmentOrClubData({
                      id: department?.node?.id ?? null,
                      slug: department?.node?.slug ?? null,
                    });
                  }}
                >
                  {department?.node?.name}
                </Button>
              ))}
            </VStack>
          </Box>

          <Box>
            {validTags.length > 0 ? (
              <Box py={3} px={4} gap={4}>
                <Text variant="mainTiny" color="text-secondary">
                  {t`TAGS`}
                </Text>
                <HStack gap={1} flexWrap="wrap" testID="events-sidebar-tags">
                  {validTags.map((tag) => {
                    const isSelected = selectedTags.includes(
                      tag?.node?.id ?? ""
                    );

                    return (
                      <EventsTag
                        isSelected={isSelected}
                        name={tag?.node?.name ?? ""}
                        count={tag?.node?.events?.count ?? 0}
                        handlePress={() => toggleTag(tag?.node?.id ?? "")}
                        key={tag?.node?.id}
                      />
                    );
                  })}
                </HStack>
              </Box>
            ) : null}
          </Box>
        </VStack>
      </ScrollView>
      <Box justifyContent="flex-end" pb={{ base: 4, sm: 2 }}>
        {confirmFilters ? (
          <Button
            accessibilityLabel={t`Apply and Filter`}
            variant="outline"
            size="small"
            onPress={confirmFilters}
            testID="events-apply-filters-button"
          >
            {t`Apply and Filter`}
          </Button>
        ) : null}
        {clearFilters ? (
          <Button
            accessibilityLabel={t`Clear filters`}
            variant="outline"
            size="small"
            onPress={clearFilters}
            testID="events-clear-filters-button"
          >
            {t`Clear filters`}
          </Button>
        ) : null}
      </Box>
      {shouldShowSearchCta ? (
        <HStack
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          justifyContent="space-between"
          borderTopWidth={1}
          borderTopColor="outline-default"
          py={4}
          px={6}
          bg="surface-primary"
        >
          <Button
            variant="secondary"
            accessibilityLabel={t`Dismiss`}
            onPress={() => {
              handleSearchChange("");
            }}
          >
            {t`Dismiss`}
          </Button>
          <Button
            accessibilityLabel={t`Search Events`}
            onPress={handleSearchPress}
          >{t`Search Events`}</Button>
        </HStack>
      ) : null}
    </VStack>
  );
};

export const NewPatronEventsSidebarFragmentString = graphql`
  fragment NewPatronEventsSidebarFragment_club on Club {
    ...useGetClubStartOfWeekNumberPatron_club
    id
    eventTags {
      edges {
        node {
          id
          name
          events(published: true) {
            count
          }
        }
      }
    }
    departments {
      edges {
        node {
          id
          name
          slug
          anyFacilityEventsEnabled
        }
      }
    }
  }
`;
